import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  School,
  MapPin,
  Map,
  Star,
  FileCheck,
  Briefcase,
  MessageCircleQuestion,
  ChevronRight,
  Settings,
  Facebook,
  Youtube,
  Instagram,
  Users,
  AlertTriangle,
  BarChart2,
  Smartphone,
  MessageCircle,
  Globe,
  ShieldCheck,
} from 'lucide-react';
import { apiRequest } from '@/utils/api-request';

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  description?: string;
  badge?: number;
  badgeVariant?: 'green' | 'red';
}

const AdminSidebar = () => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [waUnread, setWaUnread] = useState(0);
  const [waFailed, setWaFailed] = useState(0);

  useEffect(() => {
    const fetchBadge = async () => {
      try {
        const res = await apiRequest('/api/whatsapp-inbox/unread-count', { method: 'GET' });
        if (res.success) {
          setWaUnread(res.data?.unread || 0);
          setWaFailed(res.data?.failed || 0);
        }
      } catch { /* silent */ }
    };
    fetchBadge();
    const interval = setInterval(fetchBadge, 30000);
    return () => clearInterval(interval);
  }, []);

  // Admin navigation items
  const navItems: NavItem[] = [
    {
      name: 'Parent App',
      path: 'parent-app',
      icon: <Smartphone className="h-4 w-4" />,
      description: 'Manage parent app schools'
    },
    {
      name: 'Clients',
      path: 'clients',
      icon: <Users className="h-4 w-4" />,
      description: 'Manage client schools'
    },
    {
      name: 'Schools',
      path: 'schools',
      icon: <School className="h-4 w-4" />,
      description: 'Manage school profiles'
    },
    {
      name: 'Cities',
      path: 'cities',
      icon: <MapPin className="h-4 w-4" />,
      description: 'Manage cities'
    },
    {
      name: 'Areas',
      path: 'areas',
      icon: <Map className="h-4 w-4" />,
      description: 'Manage areas and localities'
    },
    {
      name: 'Reviews',
      path: 'reviews',
      icon: <Star className="h-4 w-4" />,
      description: 'Manage school reviews'
    },
    {
      name: 'Claims',
      path: 'claims',
      icon: <FileCheck className="h-4 w-4" />,
      description: 'Manage school claims'
    },
    {
      name: 'Community',
      path: 'community',
      icon: <ShieldCheck className="h-4 w-4" />,
      description: 'Moderate parent community threads'
    },
    {
      name: 'Website Sync',
      path: 'website-sync',
      icon: <Globe className="h-4 w-4" />,
      description: 'Crawl & extract school profile data'
    },
    {
      name: 'WhatsApp Inbox',
      path: 'whatsapp-inbox',
      icon: <MessageCircle className="h-4 w-4" />,
      description: 'Templates, replies & delivery status',
      badge: waFailed > 0 ? waFailed : waUnread,
      badgeVariant: waFailed > 0 ? 'red' : 'green',
    },
    {
      name: 'Jobs',
      path: 'jobs',
      icon: <Briefcase className="h-4 w-4" />,
      description: 'Manage job postings'
    },
    {
      name: 'Q & A',
      path: 'qa',
      icon: <MessageCircleQuestion className="h-4 w-4" />,
      description: 'Manage questions and answers'
    },
    {
      name: 'Counsellors',
      path: 'counsellors',
      icon: <Users className="h-4 w-4" />,
      description: 'Manage counsellors and bookings'
    },
    {
      name: 'Instagram Posts',
      path: 'instastories',
      icon: <Instagram className="h-4 w-4" />,
      description: 'Map insta Posts to schools'
    },
    {
      name: 'FB Form Mappings',
      path: 'fb-form-mappings',
      icon: <Facebook className="h-4 w-4" />,
      description: 'Map FB forms to schools'
    },
    {
      name: 'Google Campaign Mappings',
      path: 'google-campaign-mappings',
      icon: <BarChart2 className="h-4 w-4" />,
      description: 'Map Google Ads campaigns to schools'
    },
    {
      name: 'YouTube Videos',
      path: 'youtube-videos',
      icon: <Youtube className="h-4 w-4" />,
      description: 'Fetch & manage YouTube videos'
    },
    {
      name: 'Blog Misinformation',
      path: 'blog-misinformation',
      icon: <AlertTriangle className="h-4 w-4" />,
      description: 'Review misinformation reports'
    }
  ];

  // Helper function to check if a path is active
  const isPathActive = (itemPath: string) => {
    if (location.pathname.endsWith('/admin')) {
      return itemPath === 'schools';
    }

    // Remove '/admin/' prefix from current path
    const currentPath = location.pathname.replace('/admin/', '');

    // Exact match only
    return currentPath === itemPath;
  };

  // Helper function to generate the full href path
  const getFullPath = (path: string) => {
    return `/admin/${path}`;
  };

  return (
    <div className={`${isCollapsed ? 'w-16' : 'w-72'} h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 flex flex-col`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className={`flex items-center space-x-3 ${isCollapsed ? 'hidden' : ''}`}>
            <div className="bg-brandColor p-2 rounded-lg">
              <Settings className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Admin</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Platform management</p>
            </div>
          </div>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform ${!isCollapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {navItems.map((item) => {
          const isActive = isPathActive(item.path);

          return (
            <Link
              key={item.path}
              to={getFullPath(item.path)}
              className={`
                relative group flex items-center px-3 py-2 rounded-lg transition-all duration-200
                ${isActive
                  ? 'bg-gray-50 dark:bg-gray-800 text-brandColor dark:text-brandColor border-l-4 border-brandColor'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }
                ${!isCollapsed ? 'space-x-3' : 'justify-center'}
              `}
            >
              {/* Icon */}
              <div className={`flex-shrink-0 ${isActive ? 'text-brandColor dark:text-brandColor' : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`}>
                {item.icon}
              </div>

              {/* Content */}
              {!isCollapsed && (
                <div className="flex-1 flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium">{item.name}</span>
                    {item.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.description}</p>
                    )}
                  </div>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                      item.badgeVariant === 'red'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
              )}

              {/* Tooltip for collapsed state */}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                  {item.name}
                </div>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default AdminSidebar;
